import React, {createContext, useContext, useMemo} from 'react';

import {envIsPublicCloud} from '../../../config';
import {
  useEntityMrConfigQuery,
  useMigratedArtifactCollectionQuery,
  useOrgMrConfigQuery,
} from '../../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useViewer} from '../../../state/viewer/hooks';
import {OLD_MODEL_REGISTRY_PROJECT_NAME} from '../../../util/artifacts';
import {propagateErrorsContext} from '../../../util/errors';
import * as urls from '../../../util/urls';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {checkRegistryProject} from '../common/utils';
import {
  EntryPoint,
  getIsModelRegistryReadOnly,
  getModelRegistryMigrationStatus,
  MigrationStatus,
} from './utils';

export type ModelRegistryMigrationContextType = {
  entityMigrationStatus: MigrationStatus;
  isMRReadOnly: boolean;
  orgMigrationStatus: MigrationStatus;
  orgMigrationCompletedAt?: string;
  linkToMigratedArtifactCollection?: string;
  linkToMigratedRegistry?: string;
};

const initialState = {
  entityMigrationStatus: MigrationStatus.NOT_STARTED,
  isMRReadOnly: false,
  orgMigrationStatus: MigrationStatus.NOT_STARTED,
  orgMigrationCompletedAt: undefined,
  linkToMigratedArtifactCollection: undefined,
  linkToMigratedRegistry: undefined,
};

export const ModelRegistryMigrationContext =
  createContext<ModelRegistryMigrationContextType>(initialState);

export const ModelRegistryMigrationContextProvider: React.FC<{
  entryPoint: EntryPoint;
  mrEntityName?: string;
  artifactCollectionName?: string;
}> = ({entryPoint, mrEntityName, artifactCollectionName, children}) => {
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  const {
    data: orgEntityMRConfigQuery,
    loading: isOrgEntityMRConfigQueryLoading,
  } = useOrgMrConfigQuery({
    variables: {orgName: orgName},
    skip: orgName === '',
    context: propagateErrorsContext(),
  });

  const entityName = mrEntityName ?? '';
  const {data: entityMRConfigQuery, loading: isEntityMRConfigQueryLoading} =
    useEntityMrConfigQuery({
      variables: {entityName},
      skip: entityName === '',
      context: propagateErrorsContext(),
    });

  const orgEntityMRConfig =
    orgEntityMRConfigQuery?.organization?.orgEntity
      ?.modelRegistryMigrationConfig;
  const entityMRConfig =
    entityMRConfigQuery?.entity?.modelRegistryMigrationConfig;
  const {orgMigrationStatus, entityMigrationStatus} = useMemo(() => {
    if (isOrgEntityMRConfigQueryLoading || isEntityMRConfigQueryLoading) {
      return {
        orgMigrationStatus: MigrationStatus.NOT_STARTED,
        entityMigrationStatus: MigrationStatus.NOT_STARTED,
      };
    }
    return getModelRegistryMigrationStatus(orgEntityMRConfig, entityMRConfig);
  }, [
    isOrgEntityMRConfigQueryLoading,
    isEntityMRConfigQueryLoading,
    orgEntityMRConfig,
    entityMRConfig,
  ]);

  const isMRReadOnly = getIsModelRegistryReadOnly(entityMigrationStatus);
  const migrationCompletedAt =
    orgEntityMRConfig?.migrationCompletedAt != null
      ? new Date(orgEntityMRConfig?.migrationCompletedAt).toString()
      : undefined;

  const {
    data: migratedCollectionQuery,
    loading: migratedCollectionQueryLoading,
  } = useMigratedArtifactCollectionQuery({
    variables: {
      entityName,
      projectName: OLD_MODEL_REGISTRY_PROJECT_NAME,
      artifactCollectionName: artifactCollectionName ?? '',
    },
    skip: artifactCollectionName == null,
    context: propagateErrorsContext(),
  });
  const migratedCollection =
    migratedCollectionQuery?.project?.artifactCollection
      ?.modelRegistryMigratedArtifactCollection;

  let linkToMigratedArtifactCollection: string | undefined = undefined;
  let linkToMigratedRegistry: string | undefined = undefined;
  if (entryPoint !== EntryPoint.PROJECT) {
    let migratedRegistryName = undefined;
    if (!isEntityMRConfigQueryLoading) {
      const {registryName} = checkRegistryProject(
        entityMRConfig?.orgRegistryProject?.name
      );
      migratedRegistryName = registryName;
      // if the entity doesn't have a migrated registry, i.e. `registry` is undefined,
      // the url will just be for the W&B Registry homepage
      linkToMigratedRegistry = urls.globalRegistry(
        orgName,
        migratedRegistryName
      );
    }
    if (!migratedCollectionQueryLoading && migratedCollection != null) {
      // If the migrated collection is deleted after the migration, then this
      // url will point to the migrated registry's homepage.
      linkToMigratedArtifactCollection = urls.globalRegistry(
        orgName,
        migratedRegistryName,
        {
          entityName: migratedCollection.project?.entityName ?? '',
          projectName: migratedCollection.project?.name ?? '',
          collectionName: migratedCollection.name,
        }
      );
    }
  }
  const viewer = useViewer();
  const isPersonalEntity =
    entityName != null && viewer?.username === entityName;

  const contextValue = useMemo(
    () => ({
      entityMigrationStatus,
      isMRReadOnly,
      orgMigrationStatus,
      orgMigrationCompletedAt: migrationCompletedAt,
      linkToMigratedRegistry,
      linkToMigratedArtifactCollection,
    }),
    [
      entityMigrationStatus,
      isMRReadOnly,
      orgMigrationStatus,
      migrationCompletedAt,
      linkToMigratedRegistry,
      linkToMigratedArtifactCollection,
    ]
  );

  // This is to handle the internal artifacts pages so that we don't show
  // banners with the legacy mr information in the global registry context
  if (entryPoint === EntryPoint.GLOBAL_REGISTRY) {
    return (
      <ModelRegistryMigrationContext.Provider value={initialState}>
        {children}
      </ModelRegistryMigrationContext.Provider>
    );
  }

  // We currently don't support migrating personal entities/orgs.
  if (isPersonalEntity) {
    return (
      <ModelRegistryMigrationContext.Provider value={initialState}>
        {children}
      </ModelRegistryMigrationContext.Provider>
    );
  }

  return (
    <ModelRegistryMigrationContext.Provider value={contextValue}>
      {children}
    </ModelRegistryMigrationContext.Provider>
  );
};
